import { useState } from "react";
import { useIntl } from "react-intl";
import ErrorDialog from "../../../dialogs/ErrorDialog";

interface IBudgetEditActionsProps {
  hasChanges: boolean;
  submitting?: boolean;
  onSaveUpdate: () => Promise<void> | void;
  onAccept: () => Promise<void> | void;
  onReject: (rejectionObservation: string) => Promise<void> | void;
  canCounterPropose?: boolean;
  onCounterPropose?: () => Promise<void> | void;
}

export const BudgetEditActions = (props: IBudgetEditActionsProps) => {
  const intl = useIntl();
  const { hasChanges, submitting, onSaveUpdate, onAccept, onReject, canCounterPropose, onCounterPropose } = props;

  const [rejectionObservation, setRejectionObservation] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleReject = async () => {
    if (!rejectionObservation.trim()) {
      setErrorMessage(intl.formatMessage({ id: "intl-msg:budgets.rejectObservationRequired" }));
      return;
    }
    await onReject(rejectionObservation.trim());
  };

  return (
    <div className="mb-6 px-6">
      <div className="flex flex-row justify-between items-end gap-4 mt-10">
        {!hasChanges && !canCounterPropose && (
          <div className="flex flex-col flex-1">
            <div className="font-bold mb-2 text-size-12 text-gray-500">
              {intl.formatMessage({ id: "intl-msg:budgets.observation" })}
            </div>
            <input
              type="text"
              className="form-control w-full"
              placeholder={intl.formatMessage({ id: "intl-msg:budgets.rejectObservationPlaceholder" })}
              value={rejectionObservation}
              onChange={(e) => setRejectionObservation(e.target.value)}
              disabled={submitting}
            />
          </div>
        )}

        <div className={`flex flex-row gap-4 ${hasChanges || canCounterPropose ? "w-full justify-end" : "ml-8"}`}>
          {canCounterPropose && onCounterPropose ? (
            <button
              type="button"
              className="w-[250px] bg-teal-500 text-white rounded-md hover:bg-teal-600 px-6 py-2 text-size-15 font-bold disabled:opacity-50"
              onClick={onCounterPropose}
              disabled={submitting}
            >
              {intl.formatMessage({ id: "intl-msg:budgets.sendCounterProposal" })}
            </button>
          ) : hasChanges ? (
            <button
              type="button"
              className="w-[250px] bg-teal-500 text-white rounded-md hover:bg-teal-600 px-6 py-2 text-size-15 font-bold disabled:opacity-50"
              onClick={onSaveUpdate}
              disabled={submitting}
            >
              {intl.formatMessage({ id: "intl-msg:budgets.saveUpdate" })}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="w-[250px] border border-red-500 text-red-500 bg-white rounded-md hover:bg-red-50 px-6 py-2 text-size-15 font-bold disabled:opacity-50"
                onClick={handleReject}
                disabled={submitting}
              >
                {intl.formatMessage({ id: "intl-msg:budgets.reject" })}
              </button>
              <button
                type="button"
                className="w-[250px] bg-teal-500 text-white rounded-md hover:bg-teal-600 px-6 py-2 text-size-15 font-bold disabled:opacity-50"
                onClick={onAccept}
                disabled={submitting}
              >
                {intl.formatMessage({ id: "intl-msg:budgets.accept" })}
              </button>
            </>
          )}
        </div>
      </div>

      <ErrorDialog
        show={!!errorMessage}
        title={intl.formatMessage({ id: "intl-msg:budgets.reject" })}
        body={errorMessage ?? ""}
        onClose={() => setErrorMessage(null)}
      />
    </div>
  );
};
